import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { type FuzzyMatch, fuzzyFilter } from "../commands/fuzzy";
import { closeOnBackdrop } from "./dialog";
import { FileIcon } from "./FileIcon";

export interface Command {
  id: string;
  /** "View: Toggle Terminal" style: the category before the colon is shown dimmer. */
  label: string;
  /** The shorter name used in the menu bar (where the menu gives the category). */
  menuLabel?: string;
  shortcut?: string;
  /** Why it can't run right now; such commands are hidden. */
  unavailable?: string | undefined;
  run: () => void;
}

/** Quick open (files), the command palette, or a one-off pick list (e.g. language mode). */
export type PaletteState =
  | { mode: "files" | "commands" }
  | { mode: "pick"; label: string; items: Command[] };

interface CommandPaletteProps {
  /** Undefined is closed. */
  state: PaletteState | undefined;
  files: { id: string; path: string }[];
  commands: Command[];
  onOpenFile: (id: string) => void;
  onClose: () => void;
}

interface Item {
  key: string;
  text: string;
  match: FuzzyMatch;
  run: () => void;
  render: (label: ReactNode) => ReactNode;
}

const baseName = (path: string) => path.slice(path.lastIndexOf("/") + 1);

/** Bolds the matched characters. */
function highlight(text: string, positions: number[], offset = 0): ReactNode {
  const marked = new Set(positions.map((p) => p - offset));
  if (marked.size === 0) return text;
  return [...text].map((char, i) =>
    // biome-ignore lint/suspicious/noArrayIndexKey: characters of a fixed string
    marked.has(i) ? <mark key={i}>{char}</mark> : char,
  );
}

/**
 * VS Code's quick open (⌘P: files) and command palette (⌘⇧P: commands, or type ">"). Arrow keys
 * move, Enter runs, Escape closes.
 */
export function CommandPalette({
  state,
  files,
  commands,
  onOpenFile,
  onClose,
}: CommandPaletteProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [value, setValue] = useState("");
  const [active, setActive] = useState(0);
  const listId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (state) {
      setValue(state.mode === "commands" ? ">" : "");
      setActive(0);
      if (!dialog.open) dialog.showModal();
    } else if (dialog.open) {
      dialog.close();
    }
  }, [state]);

  const pick = state?.mode === "pick" ? state : undefined;
  const commandMode = !pick && value.startsWith(">");
  const search = commandMode ? value.slice(1).trim() : value.trim();
  const placeholder = pick
    ? pick.label
    : commandMode
      ? "Type a command"
      : "Search files by name (type > for commands)";

  const items: Item[] = useMemo(() => {
    if (pick || commandMode) {
      const available = (pick ? pick.items : commands).filter((c) => !c.unavailable);
      return fuzzyFilter(available, search, (c) => c.label).map(({ item, match }) => ({
        key: item.id,
        text: item.label,
        match,
        run: item.run,
        render: (label) => (
          <>
            <span className="palette-label">{label}</span>
            {item.shortcut && <kbd>{item.shortcut}</kbd>}
          </>
        ),
      }));
    }
    return fuzzyFilter(files, search, (f) => f.path).map(({ item, match }) => ({
      key: item.id,
      text: item.path,
      match,
      run: () => onOpenFile(item.id),
      render: () => {
        const name = baseName(item.path);
        const folder = item.path.slice(0, item.path.length - name.length - 1);
        return (
          <>
            <FileIcon path={item.path} />
            <span className="palette-label">
              {highlight(name, match.positions, item.path.length - name.length)}
            </span>
            {folder && <span className="palette-detail">{folder}</span>}
          </>
        );
      },
    }));
  }, [pick, commandMode, search, commands, files, onOpenFile]);

  const selected = Math.min(active, Math.max(items.length - 1, 0));
  const runItem = (item: Item | undefined) => {
    if (!item) return;
    onClose();
    // After the dialog closes, so focus moves on (e.g. into the editor or terminal).
    requestAnimationFrame(item.run);
  };

  useEffect(() => {
    document.getElementById(`${listId}-${selected}`)?.scrollIntoView({ block: "nearest" });
  }, [listId, selected]);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape closes dialogs natively; this only adds backdrop clicks for the mouse
    <dialog
      ref={dialogRef}
      className="palette"
      aria-label={pick ? pick.label : commandMode ? "Command palette" : "Go to file"}
      onClick={closeOnBackdrop}
      onClose={onClose}
    >
      <input
        className="palette-input"
        role="combobox"
        aria-expanded="true"
        aria-controls={listId}
        aria-activedescendant={items.length ? `${listId}-${selected}` : undefined}
        aria-label={placeholder}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setActive(0);
        }}
        onKeyDown={(e) => {
          const move = (by: number) => {
            e.preventDefault();
            if (items.length) setActive((selected + by + items.length) % items.length);
          };
          if (e.key === "ArrowDown") move(1);
          else if (e.key === "ArrowUp") move(-1);
          else if (e.key === "Enter") {
            e.preventDefault();
            runItem(items[selected]);
          }
        }}
      />
      <div className="palette-list" id={listId} role="listbox" aria-label="Results">
        {items.map((item, i) => (
          // biome-ignore lint/a11y/useKeyWithClickEvents: the input handles the keyboard (combobox pattern)
          <div
            key={item.key}
            tabIndex={-1}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={i === selected}
            onMouseMove={() => i !== selected && setActive(i)}
            onClick={() => runItem(item)}
          >
            {item.render(highlight(item.text, item.match.positions))}
          </div>
        ))}
        {items.length === 0 && (
          <div className="palette-empty">
            {pick || commandMode ? "No matching commands" : "No matching files"}
          </div>
        )}
      </div>
    </dialog>
  );
}
